import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Users, UserCheck, UserPlus, Briefcase, Building } from 'lucide-react';
import Button from '../../components/ui/Button';
import ContactFilters from './components/ContactFilters';
import ContactsTable from './components/ContactsTable';
import BulkActions from './components/BulkActions';
import ContactsPagination from './components/ContactsPagination';
import ContactDrawer from './components/ContactDrawer';
import EmailContactModal from './components/EmailContactModal';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import SampleDataToggle from '../../components/ui/SampleDataToggle';
import { sampleDataService } from '../../services/sampleDataService';
import { contactService } from '../../services/contactService';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeEntity } from '../../hooks/useRealtimeEntity';



export default function Contacts() {
  const { profile, isSuperAdmin } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [contactForEmail, setContactForEmail] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortConfig, setSortConfig] = useState({ key: 'fullName', direction: 'asc' });
  const [contactTypeFilter, setContactTypeFilter] = useState(''); // New: filter by contact type
  const [statusFilter, setStatusFilter] = useState('');
  const [tags, setTags] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showSampleData, setShowSampleData] = useState(false);
  const [sampleContacts, setSampleContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Replace mock data with database fetch
  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      setError('');
      const superAdmin = isSuperAdmin();
      const tenantId = superAdmin ? null : profile?.tenant_id;
      const data = await contactService?.getAllContacts(null, tenantId, superAdmin);
      setContacts(data);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Real-time contact synchronization
  const transformContact = (dbContact) => ({
    id: dbContact?.id,
    firstName: dbContact?.first_name,
    lastName: dbContact?.last_name,
    fullName: `${dbContact?.first_name} ${dbContact?.last_name}`,
    email: dbContact?.email,
    phone: dbContact?.phone,
    secondaryPhone: dbContact?.secondary_phone,
    company: dbContact?.company,
    companyName: dbContact?.company_name,
    businessName: dbContact?.business_name,
    jobTitle: dbContact?.job_title,
    contactType: dbContact?.contact_type,
    status: dbContact?.status,
    industry: dbContact?.industry,
    subscriptionStatus: dbContact?.subscription_status,
    isActive: dbContact?.is_active,
    lastContactedAt: dbContact?.last_contacted_at,
    createdAt: dbContact?.created_at,
    updatedAt: dbContact?.updated_at
  });

  const { data: realtimeContacts, setData: setRealtimeContacts, isConnected } = useRealtimeEntity(
    'contacts',
    contacts,
    transformContact
  );

  // Sync real-time data with local state
  useEffect(() => {
    if (isConnected) {
      setContacts(realtimeContacts);
    }
  }, [realtimeContacts, isConnected]);

  const handleSampleDataToggle = async (isActive) => {
    try {
      setShowSampleData(isActive);
      if (isActive) {
        const sampleData = await sampleDataService?.getSampleDataByEntity('contacts', profile?.tenant_id);
        setSampleContacts(sampleData);
      } else {
        setSampleContacts([]);
      }
    } catch (err) {
      console.error('Error loading sample data:', err);
      setError('Failed to load sample data');
    }
  };

  // Contact type statistics
  const contactStats = useMemo(() => {
    const allContacts = showSampleData ? [...contacts, ...sampleContacts] : contacts;
    return {
      all: allContacts?.length || 0,
      leads: allContacts?.filter(c => c?.contactType === 'lead')?.length || 0,
      prospects: allContacts?.filter(c => c?.contactType === 'prospect')?.length || 0,
      customers: allContacts?.filter(c => c?.contactType === 'customer')?.length || 0,
      partners: allContacts?.filter(c => c?.contactType === 'partner')?.length || 0,
      vendors: allContacts?.filter(c => c?.contactType === 'vendor')?.length || 0
    };
  }, [contacts, sampleContacts, showSampleData]);

  // Filter and sort contacts
  const filteredContacts = useMemo(() => {
    // Combine real and sample data if sample data is active
    const allContacts = showSampleData ? [...contacts, ...sampleContacts] : contacts;
    
    let filtered = allContacts?.filter((contact) => {
      const matchesSearch = searchQuery === '' ||
      contact?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      contact?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      contact?.companyName?.toLowerCase()?.includes(searchQuery?.toLowerCase());

      const matchesType = contactTypeFilter === '' || contact?.contactType === contactTypeFilter;
      const matchesStatus = statusFilter === '' || contact?.subscriptionStatus === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });

    // Sort contacts
    filtered?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'lastContactedAt') {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return sortConfig?.direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      if (typeof aValue === 'string') {
        return sortConfig?.direction === 'asc' ?
        aValue?.localeCompare(bValue) :
        bValue?.localeCompare(aValue);
      }

      return sortConfig?.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return filtered;
  }, [contacts, sampleContacts, showSampleData, searchQuery, contactTypeFilter, statusFilter, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(filteredContacts?.length / itemsPerPage);
  const paginatedContacts = filteredContacts?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Active filters count
  const activeFiltersCount = Object.values({ contactTypeFilter, statusFilter })?.filter((value) => value !== '')?.length;

  const handleMenuToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectContact = (contactId, checked) => {
    if (checked) {
      setSelectedContacts((prev) => [...prev, contactId]);
    } else {
      setSelectedContacts((prev) => prev?.filter((id) => id !== contactId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedContacts(paginatedContacts?.map((contact) => contact?.id));
    } else {
      setSelectedContacts([]);
    }
  };

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setDrawerOpen(true);
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setDrawerOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      setLoading(true);
      await contactService?.deleteContact(contactId);
      await loadContacts();
      setSuccess('Contact deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to delete contact');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (contactId, newStatus) => {
    try {
      setLoading(true);
      setError('');
      
      const contact = contacts?.find(c => c?.id === contactId);
      if (!contact) return;
      
      await contactService?.updateContact(contactId, {
        ...contact,
        status: newStatus
      });
      
      await loadContacts();
      setSuccess('Status updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async (contactData) => {
    try {
      setLoading(true);
      setError('');
      
      if (selectedContact) {
        await contactService?.updateContact(selectedContact?.id, contactData);
        setSuccess('Contact updated successfully');
      } else {
        await contactService?.createContact(contactData);
        setSuccess('Contact created successfully');
      }
      
      await loadContacts();
      setDrawerOpen(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = () => {
    setSelectedContact(null);
    setDrawerOpen(true);
  };

  const handleSubmitContact = async (e) => {
    e?.preventDefault();
    try {
      setLoading(true);
      setError('');

      await contactService?.createContact(contactData);
      
      setSuccess('Contact created successfully!');
      setIsAddModalOpen(false);
      
      // Reload contacts
      await loadContacts();
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(err?.message);
      console.error('Error creating contact:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEmail = () => {
    console.log('Sending bulk email to contacts:', selectedContacts);
    // Implement bulk email functionality
  };

  const handleBulkAssign = () => {
    console.log('Assigning to contacts:', selectedContacts);
    // Implement bulk assignment functionality
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedContacts?.length} contact(s)?`)) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      for (const contactId of selectedContacts) {
        await contactService?.deleteContact(contactId);
      }
      
      await loadContacts();
      setSelectedContacts([]);
      setSuccess(`${selectedContacts?.length} contact(s) deleted successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to delete contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleCallContact = (contact) => {
    if (!contact?.phone) {
      setError('Contact does not have a phone number');
      setTimeout(() => setError(''), 3000);
      return;
    }
    // Open phone dialer
    window.location.href = `tel:${contact?.phone}`;
  };

  const handleSmsContact = (contact) => {
    if (!contact?.phone) {
      setError('Contact does not have a phone number');
      setTimeout(() => setError(''), 3000);
      return;
    }
    // Open SMS app
    window.location.href = `sms:${contact?.phone}`;
  };

  const handleEmailContact = (contact) => {
    if (!contact?.email) {
      setError('Contact does not have an email address');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setContactForEmail(contact);
    setEmailModalOpen(true);
  };

  const handleCloseEmailModal = () => {
    setEmailModalOpen(false);
    setContactForEmail(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedContacts([]);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setSelectedContacts([]);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header 
          onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Contacts</h1>
                <p className="text-gray-600 mt-1">Manage your leads, prospects, customers, and partners</p>
              </div>
              <div className="flex gap-3">
                <SampleDataToggle 
                  entityType="contacts" 
                  onToggle={handleSampleDataToggle}
                />
                <Button onClick={handleAddContact}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Contact
                </Button>
              </div>
            </div>

            {/* Contact Type Filter Tabs */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex gap-2 overflow-x-auto">
                <button
                  onClick={() => setContactTypeFilter('')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    contactTypeFilter === '' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  All ({contactStats?.all})
                </button>
                <button
                  onClick={() => setContactTypeFilter('lead')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    contactTypeFilter === 'lead' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <UserPlus className="w-4 h-4 inline mr-2" />
                  Leads ({contactStats?.leads})
                </button>
                <button
                  onClick={() => setContactTypeFilter('prospect')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    contactTypeFilter === 'prospect' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <UserCheck className="w-4 h-4 inline mr-2" />
                  Prospects ({contactStats?.prospects})
                </button>
                <button
                  onClick={() => setContactTypeFilter('customer')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    contactTypeFilter === 'customer' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Briefcase className="w-4 h-4 inline mr-2" />
                  Customers ({contactStats?.customers})
                </button>
                <button
                  onClick={() => setContactTypeFilter('partner')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    contactTypeFilter === 'partner' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Building className="w-4 h-4 inline mr-2" />
                  Partners ({contactStats?.partners})
                </button>
                <button
                  onClick={() => setContactTypeFilter('vendor')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    contactTypeFilter === 'vendor' 
                      ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Building className="w-4 h-4 inline mr-2" />
                  Vendors ({contactStats?.vendors})
                </button>
              </div>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
                {success}
              </div>
            )}

            <ContactFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              searchTerm={searchQuery}
              filters={{ status: statusFilter, contactType: contactTypeFilter }}
              onFilterChange={(key, value) => {
                if (key === 'status') setStatusFilter(value);
                if (key === 'contactType') setContactTypeFilter(value);
              }}
              activeFiltersCount={activeFiltersCount}
              onClearFilters={() => {
                setStatusFilter('');
                setContactTypeFilter('');
              }}
            />

            {selectedContacts?.length > 0 && (
              <BulkActions
                selectedCount={selectedContacts?.length}
                onDelete={handleBulkDelete}
                onExport={() => {
                  // Handle export
                }}
                onTag={() => {
                  // Handle tagging
                }}
                onBulkEmail={handleBulkEmail}
                onBulkTag={() => {
                  // Handle tagging
                }}
                onBulkDelete={handleBulkDelete}
              />
            )}

            <ContactsTable
              contacts={paginatedContacts}
              selectedContacts={selectedContacts}
              onSelectContact={(id) => {
                setSelectedContacts(prev =>
                  prev?.includes(id) ? prev?.filter(cId => cId !== id) : [...prev, id]
                );
              }}
              onSelectAll={(checked) => {
                setSelectedContacts(checked ? filteredContacts?.map(c => c?.id) : []);
              }}
              onSelectAllContacts={(checked) => {
                setSelectedContacts(checked ? paginatedContacts?.map(c => c?.id) : []);
              }}
              onSort={(key) => {
                setSortConfig(prev => ({
                  key,
                  direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
                }));
              }}
              sortConfig={sortConfig}
              onEdit={handleEditContact}
              onDelete={handleDeleteContact}
              onStatusUpdate={handleStatusUpdate}
              onCall={handleCallContact}
              onSms={handleSmsContact}
              onEmail={handleEmailContact}
            />

            <ContactsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={filteredContacts?.length}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        </main>
      </div>

      <ContactDrawer
        contact={selectedContact}
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedContact(null);
        }}
        onSave={handleSaveContact}
      />

      <EmailContactModal
        isOpen={emailModalOpen}
        onClose={handleCloseEmailModal}
        contact={contactForEmail}
      />

      <ComponentIdBadge componentId="CRM-CONTACTS-001" />
    </div>
  );
}